import type { ComponentType, SVGProps } from 'react'
import { FormattedMessage } from 'react-intl'

import { BanknotesIcon } from '@gto/icons-react/outline/banknotes'
import { BoltSlashIcon } from '@gto/icons-react/outline/bolt-slash'
import { CurrencyDollarIcon } from '@gto/icons-react/outline/currency-dollar'
import { GiftIcon } from '@gto/icons-react/outline/gift'
import { ReceiptPercentIcon } from '@gto/icons-react/outline/receipt-percent'
import { ShieldCheckIcon } from '@gto/icons-react/outline/shield-check'
import type { BadgeProps } from '@gto/ui/badge'
import { cn } from '@gto/ui/cn'
import { Typography } from '@gto/ui/typography'

// Subset of the DS Badge color union we tint a highlight icon for. Derived from
// BadgeProps (single source of truth) via Extract so a DS color rename/removal
// fails the type-check here rather than drifting silently. The four chosen tones
// are the ones with a token in TONE_CLASS below.
type HighlightTone = Extract<
  NonNullable<BadgeProps['color']>,
  'primary' | 'info' | 'success' | 'warning'
>

type Highlight = {
  id: string
  icon: ComponentType<SVGProps<SVGSVGElement>>
  tone: HighlightTone
  /** Spans both columns on wide screens — used for the grandfather callout. */
  wide?: boolean
}

const TONE_CLASS: Record<HighlightTone, string> = {
  primary: 'bg-primary-main/10 text-primary-main',
  info: 'bg-info-main/10 text-info-main',
  success: 'bg-success-main/10 text-success-main',
  warning: 'bg-warning-main/10 text-warning-main',
}

const HIGHLIGHTS: Highlight[] = [
  { id: 'commission', icon: CurrencyDollarIcon, tone: 'primary' },
  { id: 'access', icon: GiftIcon, tone: 'info' },
  { id: 'discount', icon: ReceiptPercentIcon, tone: 'success' },
  { id: 'payments', icon: BanknotesIcon, tone: 'info' },
  { id: 'prohibited', icon: BoltSlashIcon, tone: 'warning' },
  { id: 'grandfather', icon: ShieldCheckIcon, tone: 'primary', wide: true },
]

export const HighlightsStep = () => {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2 text-center">
        <Typography
          className="font-bold"
          color="text.primary"
          variant="h5"
        >
          <FormattedMessage id="onboarding.highlights.title" />
        </Typography>
        <Typography
          color="text.secondary"
          variant="body2"
        >
          <FormattedMessage id="onboarding.highlights.subtitle" />
        </Typography>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-1">
        {HIGHLIGHTS.map(({ id, icon: Icon, tone, wide }) => (
          <div
            key={id}
            data-tst="highlight-card"
            className={cn(
              'flex items-start gap-4 rounded-lg bg-paper-elevation3 p-4 ring-1 ring-inset ring-divider',
              wide === true && 'col-span-2 sm:col-span-1',
            )}
          >
            <div
              className={cn(
                'flex size-10 shrink-0 items-center justify-center rounded-md',
                TONE_CLASS[tone],
              )}
            >
              <Icon
                height={22}
                width={22}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Typography
                className="font-bold"
                color="text.primary"
                variant="body2"
              >
                <FormattedMessage id={`onboarding.highlights.${id}.title`} />
              </Typography>
              <Typography
                color="text.secondary"
                variant="caption"
              >
                <FormattedMessage id={`onboarding.highlights.${id}.body`} />
              </Typography>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
